import { gql } from '@apollo/client';
import {
	UserType,
	State,
	Country,
	TeamMemberRole,
	SubscriptionStatus,
	PlanType,
} from '../generated';

export const getAuthedUserFlattenedUserFieldsCalculatedOrgUserFragment = gql`
	fragment getAuthedUserFlattenedUserFieldsCalculatedOrgUser on CalculatedOrgUser {
		id
		role
		phone
		isAdmin
		isOwner
		firstName
		lastName
		updatedAt
		user {
			id
			userType
			email
			onboardingCompleted
			stripeCustomerId
		}
		organization {
			id
			addressLine1
			addressLine2
			city
			state
			country
			postalCode
			createdAt
			name
			website
			phone
			contactEmail
			description
			image
			imageScale
			imageRotation
			imagePositionX
			imagePositionY
			brandedEmails
			automatedRemindersEnabled
			invoicesAllowGratuity
			canReceivePayments
			updatedAt
			facebook
			instagram
			twitter
			pinterest
			coupon {
				id
				name
			}
			groups {
				id
				name
			}
			services {
				id
				name
			}
			subscription {
				id
				status
				subscriptionPlan {
					id
					period
					type
				}
			}
			users {
				id
				role
			}
			hasStripeVerificationNeeds
			rootFolder {
				id
			}
		}
	}
`;

export const getAuthedUserFlattenedUserFieldsFragment = gql`
	fragment getAuthedUserFlattenedUserFields on AuthedUser {
		userType
		id
		clientUser {
			id
			description
			firstNameOne
			lastNameOne
			firstNameTwo
			lastNameTwo
			updatedAt
			facebook
			instagram
			twitter
			pinterest
			weddingDate
			venue
			user {
				id
				userType
				email
				onboardingCompleted
				stripeCustomerId
			}
			assignedPlanner {
				id
				firstName
				lastName
				updatedAt
				organization {
					id
					name
					updatedAt
				}
			}
		}
		orgUser {
			...getAuthedUserFlattenedUserFieldsCalculatedOrgUser
		}
		superAdmin {
			id
			firstName
			lastName
			updatedAt
			user {
				id
				userType
				email
			}
		}
		groups {
			id
			name
		}
	}
	${ getAuthedUserFlattenedUserFieldsCalculatedOrgUserFragment }
`;

/** @deprecated the FlattenedUser is probably a better definition here until we can move that flattenuser and user in redux stuff to typescript */
export type UserFlattened = {
	id: string;
	userType: UserType;
	isAdmin: boolean;
	isSuperAdmin: boolean;
	isOwner: boolean;
	isLoggedIn: true;
	groups?: { id: string; name: string }[];
};

export type OrgUser = {
	id: string;
	isLoggedIn: true;
	userType: UserType.OrgUser;
	updatedAt: string;
	image: string;
	phone: string | null;
	role: TeamMemberRole;
	organization: {
		id: string;
		createdAt: string;
		updatedAt: string;
		image: string | null;
		imageScale: number;
		imageRotation: number;
		imagePositionX: number;
		imagePositionY: number;
		contactEmail: string;
		website: string | null;
		facebook: string | null;
		instagram: string | null;
		twitter: string | null;
		pinterest: string | null;
		name: string;
		description: string | null;
		brandedEmails: boolean;
		invoicesAllowGratuity: boolean;
		users: {
			id: string;
			role: TeamMemberRole;
			firstName: string;
			lastName: string;
		}[];
		automatedRemindersEnabled: boolean;
		hasStripeVerificationNeeds: boolean;
		services: { id: string; name: string }[];
		addressLine1: string;
		addressLine2?: string;
		city: string;
		state: State;
		country: Country;
		postalCode: string;
		phone?: string;
		coupon?: { id: string; name: string };
		rootFolder?: { id: string }; // for creating new folders
		groups: { id: string; name: string }[];
		canReceivePayments: boolean;
		subscription?: {
			id: string;
			status: SubscriptionStatus;
			subscriptionPlan: {
				id: string;
				type: PlanType;
			};
		} | null;
		helloSignID: string;
		stripeID: string;
	};
	isAdmin: boolean;
	isOwner: boolean;
	firstName: string;
	lastName: string;
	contacts: any[];
	user: {
		id: string;
		email: string;
		userType: UserType.OrgUser;
		onboardingCompleted: boolean;
		stripeCustomerId: string;
	};
	groups?: { id: string; name: string }[];
};

export type ClientUser = {
	id: string;
	isLoggedIn: true;
	userType: UserType.ClientUser;
	updatedAt: string;
	image: string;
	phone: string | null;
	description: string | null;
	firstNameOne: string;
	lastNameOne: string;
	firstNameTwo: string | null;
	lastNameTwo: string | null;
	weddingDate: string | null;
	venue: string | null;
	addressLine1: string;
	addressLine2?: string;
	city: string;
	state: State;
	country: Country;
	postalCode: string;
	facebook: string | null;
	instagram: string | null;
	twitter: string | null;
	pinterest: string | null;
	user: {
		id: string;
		email: string;
		userType: UserType.ClientUser;
		onboardingCompleted: boolean;
		stripeCustomerId: string;
	};
	assignedPlanner?: {
		id: string;
		firstName: string;
		lastName: string;
		image: string;
		updatedAt: string;
		organization: {
			id: string;
			users: { id: string }[];
			services: { id: string; name: string }[];
		};
	};
	groups?: Array<string>;
};

export type SuperAdmin = {
	id: string;
	isLoggedIn: true;
	userType: UserType.SuperAdmin;
	isSuperAdmin: boolean;
	user: {
		id: string;
		email: string;
		userType: UserType.SuperAdmin;
		onboardingCompleted: boolean;
		stripeCustomerId: undefined;
	};
	groups?: Array<string>;
};

export type GuestUser = { isLoggedIn: false };

export type FlattenedUser =
	| OrgUser
	| ClientUser
	| SuperAdmin
	| GuestUser
	| undefined;

export const isOrgUserTypeguard = ( user: FlattenedUser ): user is OrgUser =>
	( user && user.isLoggedIn && user.userType === UserType.OrgUser ) || false;

export const isClientUserTypeguard = (
	user: FlattenedUser
): user is ClientUser =>
	( user && user.isLoggedIn && user.userType === UserType.ClientUser ) || false;

export type UserRegistration =
	| {
			coreUserFields?: {
				email?: string;
				password?: string;
				repassword?: string;
			};
			promoCode?: string;
			inProgress: boolean;
			invitation?: {
				id: string;
				invitationType: 'NewOrg' | 'NewOrgMember' | 'NewClient';
			};
			verificationToken?: {
				email?: string;
				id?: string;
			};
	  }
	| undefined;
